import { Laptop, Monitor, Smartphone, Tablet } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  isViewerWidthPresetId,
  VIEWER_WIDTH_PRESETS,
  type ViewerWidthPresetId,
} from './width-presets';

const ICONS: Readonly<Record<ViewerWidthPresetId, LucideIcon>> = {
  desktop: Monitor,
  laptop: Laptop,
  tablet: Tablet,
  mobile: Smartphone,
};

export interface WidthPresetControlProps {
  readonly value: ViewerWidthPresetId;
  readonly onChange: (value: ViewerWidthPresetId) => void;
}

/**
 * The width picker (spec R3.3).
 *
 * A select rather than five buttons in a row. Five labelled segments is roughly
 * 300 pixels of chrome for a control most reviewers set once and forget, and
 * every one of those pixels is taken from the prototype. Collapsed to a single
 * trigger it states the current width — which is the part that matters at a
 * glance — and costs one click when it does not.
 */
export const WidthPresetControl = ({
  value,
  onChange,
}: WidthPresetControlProps) => (
  <Select
    value={value}
    onValueChange={(next) => {
      if (isViewerWidthPresetId(next)) {
        onChange(next);
      }
    }}
  >
    <SelectTrigger
      className="h-8 w-auto gap-2 border-viewer-chrome-border bg-transparent px-2.5 text-xs font-normal text-viewer-chrome-foreground shadow-none hover:bg-accent hover:text-accent-foreground focus:ring-1"
      aria-label="Frame width"
    >
      {/* Renders the selected item's own children — icon, label and pixel hint. */}
      <SelectValue />
    </SelectTrigger>
    <SelectContent align="end">
      {VIEWER_WIDTH_PRESETS.map((preset) => {
        const PresetIcon = ICONS[preset.id];

        return (
          <SelectItem key={preset.id} value={preset.id} className="text-xs">
            <span className="flex items-center gap-2">
              <PresetIcon
                aria-hidden="true"
                className="size-3.5 shrink-0 opacity-70"
              />
              <span>{preset.label}</span>
              {preset.hint === '' ? null : (
                <span className="font-mono text-[0.65rem] text-muted-foreground">
                  {preset.hint}
                </span>
              )}
            </span>
          </SelectItem>
        );
      })}
    </SelectContent>
  </Select>
);
